import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  OnChanges,
  Input,
  SimpleChanges
} from '@angular/core';
import { ChartDataset, TooltipCallbacks } from 'chart.js';
import { _DeepPartialObject } from 'chart.js/dist/types/utils';
import { isSameDay, isSameHour } from 'date-fns';
import { AvtaleResponse } from 'src/app/services/avtale/avtale.types';
import { PrisDager, Prisomraade } from 'src/app/services/spot/spot.types';
import {
  labelTooltipCallback,
  titleTooltipCallback
} from 'src/app/tabs/forbruk/graphs/forbruk/charts/forbruk-charts-utils';
import { hasChanged } from 'src/app/utils/change-utils';
import { getHourList } from 'src/app/utils/date-utils';

@Component({
  selector: 'app-hour-spot-chart',
  templateUrl: './hour-spot-chart.component.html',
  styleUrls: ['./hour-spot-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HourSpotChartComponent implements OnInit, OnChanges {
  @Input() selectedDate: Date;
  @Input() dayPrices: PrisDager[];
  @Input() selectedArea: Prisomraade;
  @Input() extraAreas: Prisomraade[];
  @Input() avtaler: AvtaleResponse;
  @Input() stromstotteData?: PrisDager[];

  dataSets: ChartDataset<'bar' | 'line'>[];
  labels: Date[];
  title: string;
  tooltipCallbacks: _DeepPartialObject<TooltipCallbacks<'bar' | 'line'>>;
  showLegend: boolean;
  showYTitle: boolean;
  annotations: any;
  hours: number[] = getHourList();

  constructor() { }

  ngOnInit(): void {
    this.showLegend = this.extraAreas.some((x) => x.valgt) ? true : false;
    this.showYTitle = true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      this.dayPrices?.length > 0 &&
      this.selectedDate &&
      hasChanged([
        changes?.selectedArea,
        changes?.selectedDate,
        changes?.extraAreas,
        changes?.avtaler,
        changes?.dayPrices,
        changes?.stromstotteData
      ])
    ) {
      this.hours = getHourList(this.selectedDate);
      this.getChartData();
    }
  }

  getChartData() {
    this.dataSets = [];
    this.labels = [];
    this.title = 'Spotpris ' + this.selectedArea.friendly;
    this.tooltipCallbacks = {
      label: (tt) => labelTooltipCallback(tt, 'øre/kWh'),
      title: (tt) => titleTooltipCallback(tt, 'hour', 'øre/kWh')
    };
    let yMax = 100;
    const barColors = [];

    const selectedDag = this.dayPrices
      .find((dayPrice) => dayPrice.omrade === this.selectedArea.omraade)
      ?.dager.find((d) => isSameDay(this.selectedDate, new Date(d.dato)));

    let currentValue = undefined;
    const timePriserPrimary =
      selectedDag?.priser?.map((pris, i) => {
        // Safari 12 er sær på hvilket format Date konstruktøren tar imot
        // Så vi må trikse litt med tidspunktene for å få riktig tidssone
        const [dateS, timeS] = pris.tidspunkt.split('T');
        const valueHour = this.hours[i];
        const timeDato = new Date(dateS);
        timeDato.setHours(valueHour);

        if (isSameHour(timeDato, new Date())) {
          barColors.push('#0a66ff');
          currentValue = { x: timeDato, y: pris.pris };
        } else {
          barColors.push('#002987');
        }
        if (pris.pris > yMax) {
          yMax = Math.ceil(pris.pris / 100) * 100;
        }
        return {
          label: timeDato,
          price: pris.pris
        };
      }) || [];

    const ekstraOmraaderValgt = this.extraAreas.filter((x) => x.valgt);
    const timepriserSecondary =
      ekstraOmraaderValgt?.map((valgtOmraade) => {
        const extraDay = this.dayPrices
          .find((dayPrice) => dayPrice.omrade === valgtOmraade.omraade)
          ?.dager.find((day) =>
            isSameDay(this.selectedDate, new Date(day.dato))
          );
        const prices = extraDay?.priser?.map((pris) => {
          if (pris.pris > yMax) {
            yMax = Math.ceil(pris.pris / 100) * 100;
          }
          return pris.pris;
        });

        return {
          area: valgtOmraade,
          prices
        };
      }) || [];

    this.labels = timePriserPrimary.map((x) => x.label);

    if (selectedDag) {
      this.dataSets.push({
        data: timePriserPrimary.map((x) => x.price),
        label: this.selectedArea.omraade,
        order: 1, // Tegnes bakerst
        backgroundColor: barColors
      });
    }

    const stromstotteLabel = this.selectedArea.omraade + ' med strømstøtte';
    if (this.stromstotteData) {
      if (!this.dataSets.find((x) => x.label === stromstotteLabel)) {
        const selectedSSDay = this.stromstotteData
          .find((dayPrice) => dayPrice.omrade === this.selectedArea.omraade)
          ?.dager.find((d) => isSameDay(this.selectedDate, new Date(d.dato)));
        const ssData = selectedSSDay?.priser?.map((pris) => pris.pris) || [];
        this.dataSets.push({
          type: 'line',
          label: stromstotteLabel,
          fill: false,
          data: ssData,
          borderWidth: 2,
          pointRadius: 1,
          backgroundColor: '#002987',
          borderColor: '#002987',
          pointStyle: 'line',
          borderDash: [5, 5]
        });
      }
    } else {
      this.dataSets = this.dataSets.filter((x) => x.label !== stromstotteLabel);
    }

    const colors = {
      NO1: '#ff6c00',
      NO2: '#26b41b',
      NO3: '#ff80ff',
      NO4: '#002987',
      NO5: '#0a66ff'
    };

    if (timepriserSecondary.length > 0) {
      this.showLegend = true;
      timepriserSecondary.map((data) => {
        this.dataSets.push({
          type: 'line',
          label: data.area.omraade,
          fill: false,
          data: data.prices,
          borderWidth: 3,
          pointRadius: 2,
          backgroundColor: colors[data.area.omraade],
          borderColor: colors[data.area.omraade],
          pointStyle: 'circle',
          tension: 0
        });
      });
    }
    this.annotations =
      this.selectedDate.getDate() === new Date().getDate()
        ? {
          label1: {
            type: 'label',
            content: 'Nå',
            color: '#0A66FF',
            xValue: currentValue.x,
            yValue: currentValue.y < 0 ? 2 : (currentValue.y || 0) * 1.1,
            font: {
              size: 16,
              weight: 'bold'
            }
          }
        }
        : {};
  }
}
